/**
 * Ticker-scoped feed needs (feed-filters-per-view, 2026-09-12).
 *
 * A view whose rows are an explicit list of identities — the Position
 * Tracker's held positions and resting orders, a trader / org detail's book,
 * the trades ledger's history, the account plane — names them, and the feed
 * carries exactly those rows at browser cadence instead of every row of every
 * sport the list happens to touch. On 2026-09-12 (Saturday CFB) 18 of 33
 * gbuff sockets on he2 were such views, each pulling the full ~1,800
 * frames/s CFB slate to mark a handful of positions.
 *
 * The declaration is two things at once (see `FeedNeeds.tickers`):
 *
 *  1. `tickers` — the exact allow-list. It rides the wire when EVERY source
 *     on the connection is ticker-scoped; the engines then deliver only these
 *     rows, and a list of ≤ 1,024 is exempt from the edge's governor floor.
 *  2. the FOLD — what carries these rows when a ticker-BROAD sibling shares
 *     the socket (the dashboard in another tab of the same browser): the
 *     axes AND, so the allow-list cannot ride beside a sport allow-list
 *     without starving it. The fold contributes the rows' sports and games
 *     (`fold: 'view'` — a view the user is looking at) or nothing of its own
 *     (`fold: 'floor'` — the background account plane, whose off-page marks
 *     come from the core floor at its standby cadence; declaring their
 *     sports made every visited sport stay full-pipe, Dan 2026-09-02). Either
 *     way the fold opts back in the default-excluded classes the rows carry.
 *
 * Identity resolution: a Polymarket position or order may arrive as a
 * conditionId / token id rather than a slug; the store's identity indexes
 * (filled by ingest, surviving resets) resolve it. An identity the store
 * does not know yet is still listed verbatim — it matches nothing until
 * the row lands from the floor, at which point the fold re-declares — and,
 * like the account plane, it fails OPEN on class: a ticker the parser
 * cannot place in a known non-prop family opts every excluded class back in
 * (a false positive costs egress; a false negative costs a trader their
 * mark).
 */
import { useMarketStore } from '@/stores/marketStore';
import { normalizeSport } from '@/stores/marketFrameNormalization';
import { sportFromTicker } from '@/lib/kalshiTicker/sportFilter';
import { tickerMayBePlayerProp } from './playerPropTicker';
import { DEFAULT_EXCLUDED_CLASSES, type FeedNeeds } from '@/workers/feedFilter';

export interface TickerNeedsOptions {
  /** `'view'`: beside a broad sibling, carry these rows' sports + games too.
   *  `'floor'`: contribute no sport or game of your own (off-page marks come
   *  from the core floor). */
  fold: 'view' | 'floor';
  /** `'rows'` (default): opt in only the excluded classes the listed rows
   *  carry (fail-open on an unknown row). `'all'`: opt in every default-
   *  excluded class while mounted — the Position Tracker's buy panel renders
   *  prop ladders and game-event legs for a held game on click, so it keeps
   *  the class opt-in it always had; on a ticker-scoped connection the
   *  allow-list still bounds the edge, and the classes land on the floor. */
  classes?: 'rows' | 'all';
}

/** Resolve one account-plane identity to the ticker the feed knows it by. */
export function resolveFeedTicker(id: string): string {
  const market = useMarketStore.getState();
  if (market.getMarket(id)) return id;
  return market.getTickerByConditionId(id) ?? market.getTickerByTokenId(id) ?? id;
}

const EXCLUDED = new Set(DEFAULT_EXCLUDED_CLASSES.map((c) => c.toLowerCase()));

/** Pure: the declaration for these identities as of the store's current
 *  contents. Re-run when the store learns rows (see `useTickerFeedNeeds`). */
export function tickerFeedNeeds(ids: Iterable<string>, opts: TickerNeedsOptions): FeedNeeds {
  const market = useMarketStore.getState();
  const tickers = new Set<string>();
  const sports = new Set<string>();
  const games = new Set<string>();
  const classes = new Set<string>();
  let failOpen = false;
  for (const raw of ids) {
    const id = typeof raw === 'string' ? raw.trim() : '';
    if (!id) continue;
    const ticker = resolveFeedTicker(id);
    tickers.add(ticker);
    const row = market.getMarket(ticker);
    if (row) {
      const sport = row.sport ? normalizeSport(row.sport) : null;
      if (sport) sports.add(sport);
      // Rule 1/2 of the games axis (hydra 2026-09-04): the row's own wire key,
      // V2 preferred, never one composed here.
      const key = row.canonicalGameKeyV2 ?? row.canonicalGameKey ?? null;
      if (key) games.add(key);
      const cls = row.class ? row.class.toLowerCase() : null;
      if (cls && EXCLUDED.has(cls)) classes.add(cls);
    } else {
      const sport = sportFromTicker(ticker);
      if (sport) sports.add(sport);
      if (tickerMayBePlayerProp(ticker)) failOpen = true;
    }
  }
  const view = opts.fold === 'view';
  return {
    tickers: [...tickers].sort(),
    sports: view ? [...sports].sort() : [],
    games: view ? [...games].sort() : [],
    classes: failOpen || opts.classes === 'all' ? [...DEFAULT_EXCLUDED_CLASSES] : [...classes].sort(),
  };
}
